import React, { useState } from 'react';
import {
  Select,
  MenuItem,
  TextField,
  InputLabel,
  FormControl,
  Button,
  Box,
  Paper,
} from '@mui/material';

import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';

export interface StoryUnit {
  type: 'image' | 'video';
  url: string;
  duration: number;
}

interface Props {
  storiesList: StoryUnit[];
  setStoriesList: (stories: StoryUnit[]) => void;
}

export const AddImagesStory: React.FC<Props> = ({
  storiesList,
  setStoriesList,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentStory = storiesList[currentIndex];

  const handleAddStory = (): void => {
    const newStory: StoryUnit = { type: 'image', url: '', duration: 3000 };
    const updatedList = [...storiesList];
    updatedList.splice(currentIndex + 1, 0, newStory);
    setStoriesList(updatedList);
    setCurrentIndex(currentIndex + 1);
  };

  const handleRemoveStory = (): void => {
    if (storiesList.length <= 1) return;
    const updatedList = storiesList.filter((_, i) => i !== currentIndex);
    setStoriesList(updatedList);
    setCurrentIndex(prev => (prev > 0 ? prev - 1 : 0));
  };

  const handleChange = (field: keyof StoryUnit, value: string): void => {
    const updated = [...storiesList];
    const story = { ...updated[currentIndex] };

    if (field === 'duration') {
      story.duration = Number(value);
    } else if (field === 'type') {
      story.type = value as 'image' | 'video';
    } else {
      story[field] = value;
    }

    updated[currentIndex] = story;
    setStoriesList(updated);
  };

  return (
    <Paper
      elevation={3}
      sx={{
        width: '100%',
        alignSelf: 'center',
        height: 'auto',
        paddingBottom: '10px',
        marginBottom: '1rem',
        marginTop: '0.2rem',
        backgroundColor: 'rgb(194, 194, 194) !important',
      }}
    >
      <Box
        style={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ width: '94%', alignSelf: 'center' }}>
          <h3>Add stories</h3>

          <div
            style={{
              padding: '1rem',
              border: '1px solid #ccc',
              borderRadius: '8px',
              position: 'relative',
            }}
          >
            <FormControl fullWidth style={{ marginBottom: '1rem' }}>
              <InputLabel id="storyTypeSelect-label">Type</InputLabel>
              <Select
                labelId="storyTypeSelect-label"
                id="storyTypeSelect"
                name="storyType"
                label="Type"
                value={currentStory?.type}
                onChange={e => handleChange('type', e.target.value)}
              >
                <MenuItem value="image">Image</MenuItem>
                {/* <MenuItem value="video">Video</MenuItem> */}
              </Select>
            </FormControl>

            <TextField
              margin="dense"
              id="url"
              name="url"
              value={currentStory?.url}
              onChange={e => handleChange('url', e.target.value)}
              label="URL"
              type="text"
              variant="outlined"
              fullWidth
              inputProps={{ maxLength: 255 }}
            />

            <div>
              <TextField
                margin="dense"
                id="duration"
                name="duration"
                value={currentStory?.duration}
                onChange={e => handleChange('duration', e.target.value)}
                label="Duration (ms)"
                type="number"
                variant="outlined"
                fullWidth
                style={{ marginTop: '1rem' }}
              />
              <img
                src={currentStory?.url}
                alt="Preview"
                style={{ width: '100%', height: 'auto', marginTop: '1rem' }}
              />
            </div>
          </div>

          <div
            style={{
              marginBottom: '1rem',
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              alignContent: 'center',
              justifyContent: 'center',
            }}
          >
            {currentIndex + 1} / {storiesList.length}
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <Button
              variant="outlined"
              onClick={() => setCurrentIndex(i => Math.max(i - 1, 0))}
              disabled={currentIndex === 0}
            >
              <ArrowBackOutlinedIcon />
            </Button>

            <Button
              variant="outlined"
              onClick={() =>
                setCurrentIndex(i => Math.min(i + 1, storiesList.length - 1))
              }
              disabled={currentIndex === storiesList.length - 1}
            >
              <ArrowForwardOutlinedIcon />
            </Button>

            <Button variant="contained" onClick={handleAddStory}>
              Add Story
            </Button>

            <Button
              variant="contained"
              onClick={handleRemoveStory}
              disabled={storiesList.length <= 1}
            >
              Delete Story
            </Button>
          </div>
        </div>
      </Box>
    </Paper>
  );
};
